<script setup>
import { computed } from 'vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import { useSiteHostReadiness } from '@/composables/useSiteHostReadiness'

// The site's status pill, address-aware: "Live" only once castr says the
// address really serves; "Setting up address" (pulsing) while the App
// Platform edge is still routing it; "Address not ready" when that failed.
// `poll` lets this instance drive the status polling (list rows, the shell).
const props = defineProps({
	site: {
		type: Object,
		required: true,
	},
	poll: {
		type: Boolean,
		default: false,
	},
	size: {
		type: String,
		default: 'sm',
	},
})

const readiness = useSiteHostReadiness(() => props.site.id, { poll: props.poll })

const STATUS = {
	active: { label: 'Live', variant: 'success' },
	disabled: { label: 'Offline', variant: 'muted' },
	draft: { label: 'Not published yet', variant: 'warning' },
	creating: { label: 'Not published yet', variant: 'warning' },
	suspended: { label: 'Suspended', variant: 'muted' },
}

const meta = computed(() => {
	if (readiness.failed.value) return { label: 'Address not ready', variant: 'warning', pulse: false, state: 'failed' }
	if (readiness.gated.value) return { label: 'Setting up address', variant: 'accent', pulse: true, state: 'pending' }
	const base = STATUS[props.site.status] || { label: 'Offline', variant: 'muted' }
	return { ...base, pulse: props.site.status === 'active', state: 'ready' }
})
const title = computed(() => {
	if (readiness.failed.value) return readiness.detail.value || 'The site address could not be set up.'
	if (readiness.gated.value) return `${readiness.phaseLabel.value} — ${readiness.message.value}`
	return ''
})
</script>

<template>
	<StatusPill pill :size="size" :variant="meta.variant" :pulse="meta.pulse" :title="title || undefined" data-testid="site-status-chip" :data-readiness="meta.state">
		{{ meta.label }}
	</StatusPill>
</template>
